import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { CircleMarker, Polyline, useMap } from "react-leaflet";
import { connect } from "react-redux";
import { v4 as uuid } from "uuid";

import actions from "@/actions";
import { Geometry } from "@/utils";

import { LightGroupGeometry } from ".";

const MIN_VERTICES = 3;
const MAX_VERTICES = 100;
const POLY_DASH = "4";
const MARKER_RADIUS = 3;
const MARKER_OPACITY = 1;
const MARKER_ORIGIN_COLOR = "green";
const ERROR_FLASH_MS = 200;
const ERROR_COLOR = "red";

const DrawLightGroupInner = ({ map, group, setPendingGroupGeometry }) => {
    const [cursor, setCursor] = useState(null);
    const [vertices, setVertices] = useState([]);
    const [completed, setCompleted] = useState(false);
    const [error, setError] = useState(false);
    const originalCursorRef = useRef(null);

    // Use refs to store current values for use in event handlers
    // This avoids recreating callbacks and triggering useEffect cleanup
    const verticesRef = useRef(vertices);
    const completedRef = useRef(completed);
    const groupRef = useRef(group);

    useEffect(() => {
        verticesRef.current = vertices;
        completedRef.current = completed;
        groupRef.current = group;
    }, [vertices, completed, group]);

    const errorFlash = useCallback(() => {
        setError(true);
        setTimeout(() => setError(false), ERROR_FLASH_MS);
    }, []);

    const intersectsCheck = useCallback((verts, newVertex) => {
        if (verts.length < MIN_VERTICES) {
            return false;
        }
        return Geometry.containsIntersections(
            Geometry.latLngToArray([...verts, newVertex]),
        );
    }, []);

    const onMouseMove = useCallback((event) => {
        setCursor(event.latlng);
    }, []);

    const onClick = useCallback(
        (event) => {
            const currentVertices = verticesRef.current;

            if (currentVertices.length >= MAX_VERTICES) {
                errorFlash();
                return;
            }

            if (completedRef.current) {
                return;
            }

            if (intersectsCheck(currentVertices, event.latlng)) {
                errorFlash();
                return;
            }

            setVertices((v) => [...v, event.latlng]);
        },
        [intersectsCheck, errorFlash],
    );

    const onRightClick = useCallback(() => {
        if (completedRef.current) {
            return;
        }
        setVertices((v) => v.slice(0, -1));
    }, []);

    // eslint-disable-next-line react-hooks/immutability
    const onCompletePolygon = useCallback(() => {
        const currentVertices = verticesRef.current;

        if (intersectsCheck(currentVertices, currentVertices[0])) {
            errorFlash();
            return;
        }

        const finalVertices = [...currentVertices, currentVertices[0]];
        setVertices(finalVertices);
        setCompleted(true);
        setPendingGroupGeometry({
            id: uuid(),
            group: groupRef.current,
            vertices: finalVertices,
        });
        // eslint-disable-next-line react-hooks/immutability
        map._container.style.cursor = originalCursorRef.current;
    }, [intersectsCheck, errorFlash, setPendingGroupGeometry, map]);

    // eslint-disable-next-line react-hooks/immutability
    useEffect(() => {
        const mapElem = map;
        mapElem.on("mousemove", onMouseMove);
        mapElem.on("click", onClick);
        mapElem.on("contextmenu", onRightClick);
        originalCursorRef.current = mapElem._container.style.cursor;
        // eslint-disable-next-line react-hooks/immutability
        mapElem._container.style.cursor = "crosshair";

        return () => {
            mapElem.off("mousemove", onMouseMove);
            mapElem.off("click", onClick);
            mapElem.off("contextmenu", onRightClick);
            setPendingGroupGeometry(null);
        };
    }, [map, onMouseMove, onClick, onRightClick, setPendingGroupGeometry]);

    const renderPolyline = ({ vertices: verts }) => {
        let { color } = group;

        if (error) {
            color = ERROR_COLOR;
        }

        return (
            <Polyline
                interactive={false}
                positions={verts}
                pathOptions={{
                    weight: LightGroupGeometry.POLY_WEIGHT,
                    dashArray: completed || error ? "0" : POLY_DASH,
                    color: color,
                    fill: true,
                    fillColor: color,
                    fillOpacity: LightGroupGeometry.POLY_OPACITY_NEUTRAL,
                }}
            />
        );
    };

    const renderMarkers = () => {
        const verticesCopy = [...vertices];
        const markers = [];

        if (verticesCopy.length >= MIN_VERTICES) {
            markers.push(
                <CircleMarker
                    key="origin"
                    center={verticesCopy.shift()}
                    radius={MARKER_RADIUS}
                    pathOptions={{
                        color: MARKER_ORIGIN_COLOR,
                        fillColor: MARKER_ORIGIN_COLOR,
                        fillOpacity: MARKER_OPACITY,
                    }}
                    eventHandlers={{ click: onCompletePolygon }}
                />,
            );
        }

        const { color } = group;

        markers.push(
            ...verticesCopy.map((vertex) => (
                <CircleMarker
                    key={`${vertex.lat},${vertex.lng}`}
                    center={vertex}
                    radius={MARKER_RADIUS}
                    pathOptions={{
                        color,
                        fillColor: color,
                        fillOpacity: MARKER_OPACITY,
                    }}
                />
            )),
        );

        return markers;
    };

    if (completed) {
        return renderPolyline({ vertices });
    }

    return (
        vertices.length > 0 && (
            <>
                {renderMarkers()}
                {renderPolyline({ vertices })}
                {renderPolyline({
                    vertices: [vertices[vertices.length - 1], cursor],
                })}
            </>
        )
    );
};

DrawLightGroupInner.propTypes = {
    map: PropTypes.object.isRequired,
    group: PropTypes.object.isRequired,
    setPendingGroupGeometry: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = {
    setPendingGroupGeometry: actions.map.general.setPendingGroupGeometry,
};

const ConnectedDrawLightGroupInner = connect(
    mapStateToProps,
    mapDispatchToProps,
)(DrawLightGroupInner);

// Wrapper component to inject the map instance using useMap hook
/**
 * Wrapper injecting the Leaflet map instance into DrawLightGroupInner for drawing light group polygons.
 *
 * @param props - The component props passed to DrawLightGroupInner.
 * @returns The rendered draw light group wrapper.
 */
const DrawLightGroup = (props) => {
    const map = useMap();
    return <ConnectedDrawLightGroupInner {...props} map={map} />;
};
DrawLightGroup.MIN_VERTICES = MIN_VERTICES;
DrawLightGroup.MAX_VERTICES = MAX_VERTICES;
DrawLightGroup.ERROR_FLASH_MS = ERROR_FLASH_MS;
DrawLightGroup.ERROR_COLOR = ERROR_COLOR;

export default DrawLightGroup;
